import { useState } from 'react';
import type { ServicePageDraft } from './servicePageDraft';
import { parseWarrantyDays, SERVICE_TEXT_LIMITS } from './servicePageDraft';

/**
 * Spec 081a — text preview of the app's service page, modelled on OfferPhonePreview, in the mobile
 * page's order: headline → chips (warranty, duration, reviews placeholder) → description → what's
 * included → materials & details → warranty details. EN/AR toggle; sections collapse and hide when
 * empty; Arabic falls back to English per field, as the app does. Text only; the server and the
 * app stay authoritative.
 */
export interface ServicePagePreviewProps {
  name: string;
  draft: ServicePageDraft;
  thumbnailUrl?: string | null;
  /** Flat duration or the variants' min–max (from step 2); null hides the chip. */
  durationMinutes?: { min: number; max: number } | null;
}

const COPY = {
  en: {
    figure: 'Service page preview',
    toggle: 'Preview language',
    noName: 'Service name',
    about: 'About this service',
    included: "What's included",
    materials: 'Materials & details',
    warranty: 'Warranty',
    reviews: 'Reviews appear here',
    empty: 'Fill in the fields to see the service page.',
    warrantyChip: (days: number) => (days % 365 === 0 ? `${days / 365}-year warranty`
      : days % 30 === 0 ? `${days / 30}-month warranty` : `${days}-day warranty`),
    duration: (min: number, max: number) => (min === max ? `~${fmtDuration(min, 'en')}` : `${fmtDuration(min, 'en')}–${fmtDuration(max, 'en')}`),
  },
  ar: {
    figure: 'معاينة صفحة الخدمة',
    toggle: 'لغة المعاينة',
    noName: 'اسم الخدمة',
    about: 'عن هذه الخدمة',
    included: 'ما تشمله الخدمة',
    materials: 'المواد والتفاصيل',
    warranty: 'الضمان',
    reviews: 'تظهر التقييمات هنا',
    empty: 'املأ الحقول لمعاينة صفحة الخدمة.',
    warrantyChip: (days: number) => {
      const few = (n: number) => n >= 3 && n <= 10;
      if (days % 365 === 0) { const n = days / 365; return `ضمان ${n} ${few(n) ? 'سنوات' : 'سنة'}`; }
      if (days % 30 === 0) { const n = days / 30; return `ضمان ${n} ${few(n) ? 'أشهر' : 'شهر'}`; }
      return `ضمان ${days} ${few(days) ? 'أيام' : 'يوم'}`;
    },
    duration: (min: number, max: number) => (min === max ? `~${fmtDuration(min, 'ar')}` : `${fmtDuration(min, 'ar')}–${fmtDuration(max, 'ar')}`),
  },
};

function fmtDuration(minutes: number, lang: 'en' | 'ar'): string {
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  const u = lang === 'ar' ? { d: 'ي', h: 'س', m: 'د' } : { d: 'd', h: 'h', m: 'min' };
  const parts: string[] = [];
  if (d) parts.push(`${d}${u.d}`);
  if (h) parts.push(`${h}${u.h}`);
  if (m && !d) parts.push(`${m}${u.m}`);
  return parts.join(' ') || `0${u.m}`;
}

const pick = (lang: 'en' | 'ar', en: string, ar: string): string => (lang === 'ar' && ar.trim() ? ar : en).trim();

export default function ServicePagePreview({ name, draft, thumbnailUrl, durationMinutes }: ServicePagePreviewProps) {
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const c = COPY[lang];
  const headline = pick(lang, draft.headline, draft.headlineAr);
  const description = pick(lang, draft.description, draft.descriptionAr);
  const enItems = draft.whatsIncluded.map((s) => s.trim()).filter(Boolean);
  const arItems = draft.whatsIncludedAr.map((s) => s.trim()).filter(Boolean);
  const items = lang === 'ar' && arItems.length > 0 ? arItems : enItems;
  const rows = draft.materialsDetails
    .map((r) => ({ label: pick(lang, r.label, r.labelAr), value: pick(lang, r.value, r.valueAr) }))
    .filter((r) => r.label || r.value);
  const warrantyText = pick(lang, draft.warrantyDescription, draft.warrantyDescriptionAr);
  const days = parseWarrantyDays(draft.warrantyDays);
  const hasWarrantyDays = days != null && days >= SERVICE_TEXT_LIMITS.warrantyDaysMin && days <= SERVICE_TEXT_LIMITS.warrantyDaysMax;
  const isEmpty = !headline && !description && items.length === 0 && rows.length === 0 && !warrantyText && !hasWarrantyDays;

  return (
    <div className="svc-preview" data-testid="service-page-preview">
      <div className="svc-preview__toggle" role="group" aria-label={COPY.en.toggle}>
        {(['en', 'ar'] as const).map((l) => (
          <button key={l} type="button" className={`svc-preview__lang${lang === l ? ' svc-preview__lang--on' : ''}`}
            aria-pressed={lang === l} onClick={() => setLang(l)}>
            {l === 'en' ? 'EN' : 'AR'}
          </button>
        ))}
      </div>
      <div className="svc-preview__phone" role="figure" aria-label={c.figure} dir={lang === 'ar' ? 'rtl' : 'ltr'} lang={lang}>
        <div className="svc-preview__hero">
          {thumbnailUrl ? <img src={thumbnailUrl} alt="" /> : <div className="svc-preview__hero-empty" aria-hidden="true" />}
        </div>
        <div className="svc-preview__body">
          <div className="svc-preview__name">{name.trim() || c.noName}</div>
          {headline && <div className="svc-preview__headline">{headline}</div>}
          <div className="svc-preview__chips">
            {hasWarrantyDays && <span className="svc-preview__chip">{c.warrantyChip(days!)}</span>}
            {durationMinutes && durationMinutes.max > 0 && (
              <span className="svc-preview__chip"><span className="svc-preview__ltr">{c.duration(durationMinutes.min, durationMinutes.max)}</span></span>
            )}
            <span className="svc-preview__chip svc-preview__chip--muted">★ {c.reviews}</span>
          </div>

          {isEmpty && <p className="svc-preview__empty">{c.empty}</p>}

          {description && (
            <details className="svc-preview__section" open>
              <summary>{c.about}</summary>
              <p className="svc-preview__text">{description}</p>
            </details>
          )}
          {items.length > 0 && (
            <details className="svc-preview__section" open>
              <summary>{c.included}</summary>
              <ul className="svc-preview__list">
                {items.map((it, i) => <li key={i}>{it}</li>)}
              </ul>
            </details>
          )}
          {rows.length > 0 && (
            <details className="svc-preview__section" open>
              <summary>{c.materials}</summary>
              <dl className="svc-preview__table">
                {rows.map((r, i) => (
                  <div key={i} className="svc-preview__table-row">
                    <dt>{r.label}</dt>
                    <dd>{r.value}</dd>
                  </div>
                ))}
              </dl>
            </details>
          )}
          {warrantyText && (
            <details className="svc-preview__section" open data-testid="service-preview-warranty">
              <summary>{c.warranty}</summary>
              <p className="svc-preview__text">{warrantyText}</p>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
